import { useEffect, useRef, useState } from 'react';
import { Check, ChatText, PaperPlaneTilt, PencilSimple, X, XIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { useSaveProposal, type SaveProposalItem } from '~/hooks/queries/use-save-proposal';
import { useApproveSaveProposal, useRejectSaveProposal } from '~/hooks/mutations/use-save-proposal-decision';
import { approveSaveProposal, rejectSaveProposal } from './save-proposal-decide';
import {
  removeSaveLine,
  saveListBlocker,
  saveListDraftFrom,
  saveListEditFrom,
  saveListIsEmpty,
  saveTargetLabel,
  setSaveLineText,
  type SaveListDraft,
} from './save-proposal-edit-draft';

interface SaveProposalCardProps {
  sessionId: string;
  /** Fires after a decision — used by the parent to nudge the agent with a
   *  synthetic turn so it acts right away. For 'approve'/'reject' this is the
   *  existing behaviour (fired after the backend confirms the decision); for
   *  'comment' the parent sends the user's corrections back so the agent
   *  re-proposes a corrected save list (FRONT-1148, mirrors PlanCard).
   *  FRONT-2321: fires only once the server actually decided, and an
   *  edited approve carries `editedItems` — the list as stored — so the
   *  parent tells Eddy to save exactly those. */
  onDecision?: (
    decision: 'approve' | 'reject' | 'comment',
    opts?: { comment?: string; editedItems?: SaveProposalItem[] },
  ) => void;
  /** True while the assistant turn is still streaming. Unlike a plan
   *  (propose_plan, which ends the model's turn), propose_saves does NOT end
   *  the turn — so the redraft this card sends via onDecision('comment') can
   *  otherwise be dispatched into a still-in-flight turn. Blocked the same
   *  way FRONT-196 blocks PlanCard for the analogous reason. */
  turnActive?: boolean;
}

/**
 * FRONT-1148. Mirrors PlanCard's planActionsBlocked: the Approve / Reject /
 * Add-comments actions are blocked while a decision request is in flight, a
 * redraft is pending, or the assistant turn is still streaming.
 */
export function saveProposalActionsBlocked(s: {
  approvePending: boolean;
  rejectPending: boolean;
  reworking: boolean;
  turnActive: boolean;
}): boolean {
  return s.approvePending || s.rejectPending || s.reworking || s.turnActive;
}

interface SaveProposalActionsProps {
  reworking: boolean;
  turnActive: boolean;
  commenting: boolean;
  comment: string;
  setComment: (value: string) => void;
  busy: boolean;
  onSendComment: () => void;
  onCancelComment: () => void;
  onStartComment: () => void;
  onApprove: () => void;
  onReject: () => void;
  onStartEdit: () => void;
}

/**
 * The reworking/commenting/decision states are mutually exclusive — exactly
 * one renders at a time, same shape as PlanCard's PlanActions.
 */
function SaveProposalActions({
  reworking,
  turnActive,
  commenting,
  comment,
  setComment,
  busy,
  onSendComment,
  onCancelComment,
  onStartComment,
  onApprove,
  onReject,
  onStartEdit,
}: SaveProposalActionsProps) {
  if (reworking) {
    return (
      <div className="pt-1 text-sm text-muted-foreground">
        Redrafting the save proposal with your corrections…
      </div>
    );
  }
  if (turnActive) {
    return (
      <div className="pt-1 text-sm text-muted-foreground">
        Waiting for the agent to finish…
      </div>
    );
  }
  if (commenting) {
    return (
      <div className="pt-1">
        <textarea
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              onSendComment();
            }
          }}
          rows={3}
          placeholder="What should change? Add your corrections and the save list will be redrafted…"
          className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex gap-2 pt-2">
          <Button
            onClick={onSendComment}
            disabled={!comment.trim() || busy}
            variant="ghost"
            size="sm"
            className="bg-chart-4/20 text-chart-4 hover:brightness-110"
          >
            <PaperPlaneTilt size={14} weight="bold" /> Send to agent
          </Button>
          <Button
            onClick={onCancelComment}
            variant="ghost"
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2 pt-1">
      <Button
        onClick={onApprove}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="bg-success/20 text-success hover:brightness-110"
      >
        <Check size={14} weight="bold" /> Approve
      </Button>
      <Button
        onClick={onReject}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="bg-destructive/20 text-destructive hover:brightness-110"
      >
        <X size={14} weight="bold" /> Reject
      </Button>
      <Button
        onClick={onStartComment}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
      >
        <ChatText size={14} weight="bold" /> Add comments
      </Button>
      {/* FRONT-2321: yes-first (Approve · Reject), then the two correction
          paths — Add comments has Eddy redraft, Edit fixes it in place. */}
      <Button
        onClick={onStartEdit}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
      >
        <PencilSimple size={14} weight="bold" /> Edit
      </Button>
    </div>
  );
}

/**
 * FRONT-2321: the card in edit mode. Each line can be reworded or removed;
 * its Knowledge/Note pill is fixed. With every line removed there is nothing
 * left to save, so the primary action becomes the Reject it now is. The
 * disabled Save and its sentence are one decision (`saveListBlocker`).
 */
export function SaveListEditPanel({
  draft,
  onChange,
  busy,
  deciding,
  waiting,
  refusal,
  failed,
  onSave,
  onReject,
  onCancel,
}: {
  draft: SaveListDraft;
  onChange: (next: SaveListDraft) => void;
  busy: boolean;
  /** This card's own approve/reject is in flight. */
  deciding: boolean;
  /** The assistant turn is still streaming — Save waits for it (FRONT-1148). */
  waiting: boolean;
  refusal: string | null;
  failed: boolean;
  onSave: () => void;
  onReject: () => void;
  onCancel: () => void;
}) {
  const empty = saveListIsEmpty(draft);
  const blocker = saveListBlocker(draft);
  return (
    <div data-testid="save-proposal-edit" className="space-y-2">
      <p className="text-[length:var(--text-sm)] font-medium text-foreground">Edit before saving</p>
      {empty ? (
        <p className="rounded-lg bg-background/70 px-3 py-2 text-sm text-muted-foreground text-pretty">
          Every line is removed, so saving this counts as Reject.
        </p>
      ) : (
        <ul className="max-h-[35vh] space-y-1.5 overflow-y-auto overscroll-contain pr-1">
          {draft.lines.map((line) => (
            <li key={line.index} className="flex items-start gap-1.5">
              <span className="mt-2 shrink-0 rounded bg-muted px-1.5 py-0.5 text-[length:var(--text-2xs)] font-medium uppercase tracking-wide text-muted-foreground">
                {saveTargetLabel(line.target)}
              </span>
              <Textarea
                rows={1}
                aria-label="Line to save"
                className="min-h-8 max-h-40 resize-none py-1.5"
                value={line.text}
                aria-invalid={!line.text.trim() || undefined}
                onChange={(e) => onChange(setSaveLineText(draft, line.index, e.target.value))}
              />
              <Button
                size="icon"
                variant="ghost"
                className="relative size-8 shrink-0 text-muted-foreground hover:text-foreground after:absolute after:-inset-1 after:content-['']"
                aria-label="Remove this line"
                onClick={() => onChange(removeSaveLine(draft, line.index))}
              >
                <XIcon size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {blocker && <p className="text-sm text-destructive">{blocker}</p>}
      {waiting && <p className="text-sm text-muted-foreground">Waiting for the agent to finish…</p>}
      {refusal && <p className="text-sm text-destructive">{refusal}</p>}
      {failed && (
        <p className="text-sm text-destructive text-pretty">
          That didn't go through, and nothing was saved. The list may have changed: cancel to see the current one.
        </p>
      )}
      <div className="flex gap-2 pt-1">
        {empty ? (
          <Button
            onClick={onReject}
            disabled={busy}
            variant="ghost"
            size="sm"
            className="bg-destructive/20 text-destructive hover:brightness-110"
          >
            <X size={14} weight="bold" /> Reject
          </Button>
        ) : (
          <Button
            onClick={onSave}
            disabled={busy || blocker !== null}
            variant="ghost"
            size="sm"
            className="bg-success/20 text-success hover:brightness-110"
          >
            <Check size={14} weight="bold" /> {deciding ? 'Saving…' : 'Save this version'}
          </Button>
        )}
        {/* Backing out never waits on an unrelated turn — only on this card's own request. */}
        <Button onClick={onCancel} disabled={deciding} variant="ghost" size="sm">
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function SaveProposalCard({ sessionId, onDecision, turnActive = false }: SaveProposalCardProps) {
  const { data: proposal } = useSaveProposal(sessionId);
  const approve = useApproveSaveProposal();
  const reject = useRejectSaveProposal();

  // Comment flow: an inline notes box, and a brief "reworking" state after the
  // user sends corrections — held until a NEW proposal draft arrives
  // (different createdAt), with a safety timeout so the card can never get
  // stuck. Same shape as PlanCard's comment flow.
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState('');
  const [reworking, setReworking] = useState(false);
  const sentForCreatedAtRef = useRef<string | null>(null);

  // FRONT-2321: the Edit form. While it is open the card stays mounted even
  // if the proposal stops being pending (decided in another tab), so Save
  // can report that it didn't go through instead of the form vanishing.
  const [draft, setDraft] = useState<SaveListDraft | null>(null);
  const [editRefusal, setEditRefusal] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const proposalCreatedAt = proposal?.createdAt;
  useEffect(() => {
    if (!reworking) return;
    // A fresh draft arrived → clear the reworking state and reset the box.
    if (proposalCreatedAt && proposalCreatedAt !== sentForCreatedAtRef.current) {
      setReworking(false);
      setCommenting(false);
      setComment('');
      return;
    }
    // Safety net: never leave the card stuck if no new draft shows up.
    const t = setTimeout(() => setReworking(false), 90_000);
    return () => clearTimeout(t);
  }, [reworking, proposalCreatedAt]);

  if (!draft && (!proposal || proposal.status !== 'pending')) return null;

  const busy = saveProposalActionsBlocked({
    approvePending: approve.isPending,
    rejectPending: reject.isPending,
    reworking,
    turnActive,
  });

  const callbacks = {
    onDecided: (d: 'approve' | 'reject', opts?: { editedItems?: SaveProposalItem[] }) => {
      setDraft(null);
      onDecision?.(d, opts);
    },
    onStale: () => setFailed(true),
    onFailed: () => setFailed(true),
    onRefused: setEditRefusal,
  };
  const startDecision = () => {
    setFailed(false);
    setEditRefusal(null);
  };
  const handleApprove = () => {
    startDecision();
    approveSaveProposal({ approve: approve.mutate, sessionId, ...callbacks });
  };
  const handleReject = () => {
    startDecision();
    rejectSaveProposal({ reject: reject.mutate, sessionId, ...callbacks });
  };
  const handleSaveEdit = () => {
    if (!draft) return;
    startDecision();
    approveSaveProposal({ approve: approve.mutate, sessionId, edit: saveListEditFrom(draft), ...callbacks });
  };
  const handleSendComment = () => {
    const text = comment.trim();
    if (!text || !proposal) return;
    sentForCreatedAtRef.current = proposal.createdAt ?? null;
    setReworking(true);
    onDecision?.('comment', { comment: text });
  };

  return (
    <div className="px-4 py-3 mb-2 rounded-xl border border-border bg-muted/50">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[length:var(--text-2xs)] uppercase tracking-wide bg-chart-4/20 text-chart-4 px-1.5 py-0.5 rounded font-medium">
          Save
        </span>
      </div>

      {proposal?.recap && (
        <p className="text-sm text-muted-foreground mb-2">{proposal.recap}</p>
      )}

      {draft ? (
        <SaveListEditPanel
          draft={draft}
          onChange={setDraft}
          busy={busy}
          deciding={approve.isPending || reject.isPending}
          waiting={turnActive}
          refusal={editRefusal}
          failed={failed}
          onSave={handleSaveEdit}
          onReject={handleReject}
          onCancel={() => {
            setDraft(null);
            setEditRefusal(null);
            setFailed(false);
          }}
        />
      ) : proposal && (
        <>
          {/* FRONT-920: cap and internally scroll the (potentially long) items
              list so it can never push the actions below the chat modal's own
              clip line — mirrors the same fix on PlanCard. */}
          {proposal.items.length > 0 && (
            <div className="mb-2 max-h-[35vh] overflow-y-auto overscroll-contain pr-1">
              <div className="text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground mb-1">Items to save</div>
              <ul className="space-y-1">
                {proposal.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="flex-1">{item.text}</span>
                    <span className="text-[length:var(--text-2xs)] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium shrink-0">
                      {saveTargetLabel(item.target)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SaveProposalActions
            reworking={reworking}
            turnActive={turnActive}
            commenting={commenting}
            comment={comment}
            setComment={setComment}
            busy={busy}
            onSendComment={handleSendComment}
            onCancelComment={() => {
              setCommenting(false);
              setComment('');
            }}
            onStartComment={() => setCommenting(true)}
            onApprove={handleApprove}
            onReject={handleReject}
            onStartEdit={() => {
              startDecision();
              setDraft(saveListDraftFrom(proposal));
            }}
          />
          {failed && (
            <p className="pt-2 text-sm text-destructive">That didn't go through, and nothing was changed.</p>
          )}
        </>
      )}
    </div>
  );
}
